import { AlmconnectionAdoApiClient } from "@/natsstubs/almconnection/ado/client";
import type {
  AdoArtMappingSource,
  AdoTeamMappingSource,
} from "@/natsstubs/almconnection/ado/models";
import { AlmconnectionCommonApiClient } from "@/natsstubs/almconnection/common/client";
import { AlmconnectionJiraApiClient } from "@/natsstubs/almconnection/jira/client";
import type { SetJiraAuthMethodRequest } from "@/natsstubs/almconnection/jira/models";
import { AlmconnectionRallyApiClient } from "@/natsstubs/almconnection/rally/client";
import { AlmsyncUserMappingsApiClient } from "@/natsstubs/almsync/user_mappings/client";
import type { NoSuccessError } from "@/natsstubs/wire/error";
import { keysToCamel, keysToSnake } from "@/natsstubs/wire/transform";

import type { NatsRpcBackend } from "../NatsRpcBackend";
import type { UnknownRecord } from "../RpcBackend";
import { mapTypedError } from "./mapTypedError";

function connectionId(args: unknown[], kwargs?: UnknownRecord): string {
  return String(kwargs?.connection_id ?? args[0]);
}

export function registerAlmCommonAdapters(nats: NatsRpcBackend): void {
  const common = new AlmconnectionCommonApiClient(nats.caller);
  const jira = new AlmconnectionJiraApiClient(nats.caller);
  const ado = new AlmconnectionAdoApiClient(nats.caller);
  const rally = new AlmconnectionRallyApiClient(nats.caller);
  const userMappings = new AlmsyncUserMappingsApiClient(nats.caller);

  const scalarOps = {
    delete: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        await common.deleteConnection(connectionId(args, kwargs));
        return { success: true };
      } catch (e) {
        if (
          (e as { code?: string })?.code === "ALMCONNECTION_COMMON__ALM_CONNECTION_IN_USE_ERROR"
        ) {
          const details = (e as NoSuccessError).response?.details as
            | { session_ids?: unknown[] }
            | undefined;
          const sessionIds = Array.isArray(details?.session_ids)
            ? (details.session_ids as string[])
            : [];
          return { success: false, sessions_ids: sessionIds };
        }
        throw e;
      }
    },

    get_name: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await common.getName(connectionId(args, kwargs))).name,

    set_name: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setName({ name: kwargs?.name as string }, connectionId(args, kwargs)),

    get_url: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await common.getUrl(connectionId(args, kwargs))).url,

    set_url: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setUrl({ url: kwargs?.url as string }, connectionId(args, kwargs)),

    get_timeout: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await common.getTimeout(connectionId(args, kwargs))).timeout,

    set_timeout: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setTimeout({ timeout: kwargs?.timeout as number }, connectionId(args, kwargs)),

    get_ignore_webhooks_fired_by_alm_user: async (args: unknown[], kwargs?: UnknownRecord) => ({
      data: (await common.getIgnoreWebhooksFiredByAlmUser(connectionId(args, kwargs)))
        .ignoreWebhooksFiredByAlmUser,
    }),

    set_ignore_webhooks_fired_by_alm_user: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setIgnoreWebhooksFiredByAlmUser(
        { ignoreWebhooksFiredByAlmUser: kwargs?.ignore_webhooks_fired_by_alm_user as boolean },
        connectionId(args, kwargs),
      ),

    get_tls_verification: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await common.getTlsVerification(connectionId(args, kwargs))).tlsVerification,

    set_tls_verification: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setTlsVerification(
        { tlsVerification: kwargs?.do_verify as boolean },
        connectionId(args, kwargs),
      ),

    get_disable_delete: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await common.getDisableDelete(connectionId(args, kwargs))).disableDelete,

    set_disable_delete: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.setDisableDelete(
        { disableDelete: kwargs?.disable_delete as boolean },
        connectionId(args, kwargs),
      ),

    get_certificate_info: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        // rawResponse subject: the stub returns the raw body
        // ({ certificate_name, not_after }) or null (no cert) untouched — already
        // the same shape WAMP returns, so pass it through.
        return await common.getCertificateInfo(connectionId(args, kwargs), String(kwargs?.type));
      } catch (e) {
        // Converge on the WAMP `{ error: <code> }` shape for the two unparseable
        // cases. No fallback → unknown errors rethrow.
        return mapTypedError(e, {
          ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR: () => ({
            error: "ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR",
          }),
          ALMCONNECTION_COMMON__CERTIFICATE_UNREADABLE_ERROR: () => ({
            error: "ALMCONNECTION_COMMON__CERTIFICATE_UNREADABLE_ERROR",
          }),
        });
      }
    },

    delete_certificate: async (args: unknown[], kwargs?: UnknownRecord) =>
      common.deleteCertificate(connectionId(args, kwargs), String(kwargs?.type)),

    set_certificate: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        await common.setCertificate(
          { certificate: kwargs?.data as string, name: kwargs?.name as string },
          connectionId(args, kwargs),
          String(kwargs?.type),
        );
        return { success: true };
      } catch (e) {
        return mapTypedError(
          e,
          {
            ALMCONNECTION_COMMON__CERTIFICATE_TOO_LARGE_ERROR: (error) => ({
              success: false,
              error,
            }),
          },
          () => ({ success: false, error: "Could not save the certificate." }),
        );
      }
    },

    set_passphrase: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        await common.setPassphrase(
          { passphrase: kwargs?.passphrase as string },
          connectionId(args, kwargs),
        );
        return { success: true };
      } catch (e) {
        return mapTypedError(
          e,
          {
            ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR: () => ({
              success: false,
              errorCode: 1,
              error: "Could not save the certificate. Wrong passphrase.",
            }),
          },
          () => ({
            success: false,
            errorCode: 2,
            error: "Could not save the certificate. Server problem.",
          }),
        );
      }
    },

    get_team_mapping_sources: async (args: unknown[]) => ({
      data: keysToSnake(
        (await common.getTeamMappingSources(String(args[0]), Number(args[1]))).sources,
      ),
    }),

    set_team_mapping_sources: async (args: unknown[]) =>
      common.setTeamMappingSources(
        { sources: args[2] as UnknownRecord[] },
        String(args[0]),
        Number(args[1]),
      ),

    get_art_mapping_sources: async (args: unknown[]) => ({
      data: keysToSnake(
        (await common.getArtMappingSources(String(args[0]), Number(args[1]))).sources,
      ),
    }),

    set_art_mapping_sources: async (args: unknown[]) =>
      common.setArtMappingSources(
        { sources: args[2] as UnknownRecord[] },
        String(args[0]),
        Number(args[1]),
      ),
  };

  const canConnect = (client: { canConnect(cid: string): Promise<{ canConnect: boolean }> }) => ({
    can_connect: async (args: unknown[], kwargs?: UnknownRecord) => ({
      ok: (await client.canConnect(connectionId(args, kwargs))).canConnect,
      error: "",
    }),
  });

  nats.register("alm.jira", "alm-tools", {
    ...scalarOps,
    ...canConnect(jira),
    get_auth_method: async (args: unknown[]) =>
      (await jira.getJiraAuthMethod(String(args[0]))).authMethod,
    set_auth_method: async (args: unknown[]) =>
      jira.setJiraAuthMethod(
        { authMethod: args[1] as SetJiraAuthMethodRequest["authMethod"] },
        String(args[0]),
      ),
    get_username: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await jira.getJiraUsername(connectionId(args, kwargs))).username,
    set_username: async (args: unknown[], kwargs?: UnknownRecord) =>
      jira.setJiraUsername({ username: kwargs?.username as string }, connectionId(args, kwargs)),
  });

  nats.register("alm.ado", "alm-tools", {
    ...scalarOps,
    ...canConnect(ado),
    get_username: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await ado.getAdoUsername(connectionId(args, kwargs))).username,
    set_username: async (args: unknown[], kwargs?: UnknownRecord) =>
      ado.setAdoUsername({ username: kwargs?.username as string }, connectionId(args, kwargs)),

    // ADO-specific mapping-sources subjects: unlike the shared subjects in
    // scalarOps, the backend converts area paths between the internal
    // forward-slash form and the ADO-native backslash form.
    get_team_mapping_sources: async (args: unknown[]) => ({
      data: keysToSnake(
        (await ado.getAdoTeamMappingSources(String(args[0]), Number(args[1]))).sources,
      ),
    }),

    set_team_mapping_sources: async (args: unknown[]) =>
      ado.setAdoTeamMappingSources(
        // The typed stub payload is camelCase (the caller keysToSnakes it onto
        // the wire), while the WAMP-shaped caller hands us snake_case sources.
        { sources: keysToCamel(args[2] as AdoTeamMappingSource[]) },
        String(args[0]),
        Number(args[1]),
      ),

    get_art_mapping_sources: async (args: unknown[]) => ({
      data: keysToSnake(
        (await ado.getAdoArtMappingSources(String(args[0]), Number(args[1]))).sources,
      ),
    }),

    set_art_mapping_sources: async (args: unknown[]) =>
      ado.setAdoArtMappingSources(
        { sources: keysToCamel(args[2] as AdoArtMappingSource[]) },
        String(args[0]),
        Number(args[1]),
      ),
  });

  // `ac` (Agile Central) is served by the Rally stubs. apikey is Rally-only.
  nats.register("alm.ac", "alm-tools", {
    ...scalarOps,
    ...canConnect(rally),
    get_apikey: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await rally.getRallyApikey(connectionId(args, kwargs))).apikey,
    set_apikey: async (args: unknown[], kwargs?: UnknownRecord) =>
      rally.setRallyApikey({ apikey: kwargs?.apikey as string }, connectionId(args, kwargs)),
  });

  for (const type of ["jira", "ado", "ac"]) {
    nats.register(`alm.${type}`, "nats-only", {
      get_user_mapping_settings: async (args: unknown[], kwargs?: UnknownRecord) =>
        (await userMappings.getSettings(connectionId(args, kwargs))).settings,

      set_user_mapping_settings: async (args: unknown[], kwargs?: UnknownRecord) =>
        userMappings.setSettings(
          { settings: { auto: kwargs?.auto as boolean, manual: kwargs?.manual as boolean } },
          connectionId(args, kwargs),
        ),
    });
  }
}
